// Builder stepper validation — PSD §3.6, P1.18 (F12.1). State layer.

import type { ValuationInputs } from '@backsolve/shared-types';
import type { ValuationRecord } from '../persistence/schema';

export interface StepValidation {
  step: number;
  valid: boolean;
  blockingIssues: string[];
}

/** Per-step blocking rules for the 7-step Builder. */
export function computeStepValidation(step: number, record: ValuationRecord | null): StepValidation {
  const blockingIssues: string[] = [];
  if (!record) {
    return { step, valid: false, blockingIssues: ['NO_ACTIVE_VALUATION'] };
  }
  const inputs = record.inputs;
  switch (step) {
    case 1: {
      // Setup & dates
      if (!inputs.compliance.valuationDate) blockingIssues.push('VALUATION_DATE_REQUIRED');
      break;
    }
    case 2: {
      // Cap-table selection
      if (inputs.capTableSnapshot.instruments.length === 0) blockingIssues.push('EMPTY_CAP_TABLE');
      break;
    }
    case 3: {
      // Equity value & anchor
      if (inputs.equityValue.source === 'backsolve') {
        if (!inputs.equityValue.anchorInstrumentId) blockingIssues.push('ANCHOR_INSTRUMENT_REQUIRED');
        if (!inputs.equityValue.anchorPricePerShare || inputs.equityValue.anchorPricePerShare <= 0) {
          blockingIssues.push('ANCHOR_PRICE_REQUIRED');
        }
      } else {
        if (!inputs.equityValue.directEquityValue || inputs.equityValue.directEquityValue <= 0) {
          blockingIssues.push('DIRECT_EQUITY_VALUE_REQUIRED');
        }
      }
      break;
    }
    case 4: {
      // Methodology — always valid (default opm)
      break;
    }
    case 5: {
      // Assumptions
      if (!(inputs.assumptions.volatility > 0)) blockingIssues.push('VOLATILITY_REQUIRED');
      if (!(inputs.assumptions.timeToLiquidity > 0)) blockingIssues.push('TIME_TO_LIQUIDITY_REQUIRED');
      if (inputs.assumptions.riskFreeRate < 0) blockingIssues.push('RISK_FREE_INVALID');
      break;
    }
    case 6: {
      // Discounts
      if (inputs.discounts.dlomPercentage < 0 || inputs.discounts.dlomPercentage >= 1) {
        blockingIssues.push('DLOM_PERCENTAGE_INVALID');
      }
      break;
    }
    case 7: {
      // Run — valid if steps 1-6 valid
      break;
    }
    default:
      break;
  }
  return { step, valid: blockingIssues.length === 0, blockingIssues };
}

/** Are all steps 1..6 valid (gate for Run on step 7)? */
export function allPriorStepsValid(record: ValuationRecord | null, throughStep: number): boolean {
  for (let s = 1; s <= throughStep; s++) {
    if (!computeStepValidation(s, record).valid) return false;
  }
  return true;
}

export type { ValuationInputs };
